import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { FavoriteItem, FavoriteItemType } from '../../../modules/auth'
import { Locale } from '../../../modules/locale'
import { Font } from '../../../constants'
import MainModule from '../../../modules/main'

Locale.getInstance().registerKeySet('fr', 'favoriteButton', {
  follow: 'Favoris',
  following: 'Favoris',
})

Locale.getInstance().registerKeySet('en', 'favoriteButton', {
  follow: 'Favourite',
  following: 'Favourite',
})

export interface LvcFavoriteButton {
  dataId: number
  dataType: FavoriteItemType
  liked: boolean
  dataWidth: string
  dataButtonSize: 'large' | 'medium'
  dataButtonColor: 'blue' | 'white'
  dataButtonMobileFloat: true | false
  dataGtmId: string
  dataGtmName: string
  dataGtmBrand: string
  dataGtmCategory: string
  dataGtmCategory2: string
  dataGtmCategory3: string
  dataGtmCategory4: string
}

interface FavoriteStatusUpdateEvent {
  type: FavoriteItemType
  id: number
  favorite: true
}

export class LvcFavoriteButton extends MinzeElement {
  attrs: Attrs = [
    'data-id',
    'data-width',
    'data-type',
    'data-button-size',
    'data-button-color',
    'data-button-mobile-float',
    'data-gtm-id',
    'data-gtm-name',
    'data-gtm-brand',
    'data-gtm-category',
    'data-gtm-category2',
    'data-gtm-category3',
    'data-gtm-category4',
  ]
  reactive: Reactive = [['liked', false]]
  onClick = (event: Event) => {
    event.preventDefault()
    event.stopPropagation()
    const mainModule = MainModule.getInstance()
    if (!this.liked) {
      this.gtmData = {
        event: 'add_to_wishlist',
        ecommerce: {
          items: [
            {
              item_id: typeof this.dataId === 'boolean' ? '' : this.dataId || '', //id
              item_name: typeof this.dataGtmName === 'boolean' ? '' : this.dataGtmName || '', //name
              item_brand: typeof this.dataGtmBrand === 'boolean' ? '' : this.dataGtmBrand || '', //artist
              item_category:
                typeof this.dataGtmCategory === 'boolean' ? '' : this.dataGtmCategory || '', //category
              item_category2:
                typeof this.dataGtmCategory2 === 'boolean' ? '' : this.dataGtmCategory2 || '', //place
              item_category3:
                typeof this.dataGtmCategory3 === 'boolean' ? '' : this.dataGtmCategory3 || '', //date
              item_category4:
                typeof this.dataGtmCategory4 === 'boolean' ? '' : this.dataGtmCategory4 || '', // gratuit | billet
            },
          ],
        },
      }
      mainModule.analyticsSetCustomData(this.gtmData)
    }
    mainModule.getAuth().saveItemToFavorites(this.dataId, this.dataType)
  }

  updateButton = (isLiked: boolean) => {
    this.liked = isLiked
    this.rerender(true)
  }

  onFavoriteStatusUpdate = (event: Event) => {
    const detail = (event as CustomEvent<FavoriteStatusUpdateEvent>).detail

    if (this.dataId === detail.id && this.dataType === detail.type) {
      this.updateButton(detail.favorite)
    }
  }

  updateFavoritesData(event: Event, dataId: number, dataType: FavoriteItemType) {
    const detail = (event as CustomEvent).detail
    const favorites: FavoriteItem[] = detail.favorites
    this.updateButton(!!favorites.find(item => item.id === dataId && item.type === dataType))
  }

  onReady() {
    const mainModule = MainModule.getInstance()
    const favorites: FavoriteItem[] = mainModule.getAuth().getFavorites()
    this.updateButton(
      !!favorites.find(item => item.id === this.dataId && item.type === this.dataType)
    )
  }

  html = () => `
    <div id="button" class="lvc-button-medium ${this.dataButtonColor === 'white' ? '' : 'isblue'} ${this.liked ? 'liked' : ''}">
      <div class="lvc-button-medium_text">
        <span class="unliked">${Locale.getKey('favoriteButton.follow')}</span>
        <span class="liked">${Locale.getKey('favoriteButton.following')}</span>
      </div>
      <div class="lvc-button-medium_icon">
        <div class="unliked icon">
          <lvc-favorite-heart-icon data-size="${this.dataButtonSize === 'large' ? '44' : '33'}" data-color="none"></lvc-favorite-heart-icon>
        </div>
        <div class="liked icon">
          <lvc-favorite-heart-icon data-size="${this.dataButtonSize === 'large' ? '44' : '33'}" data-color="#FF4600"></lvc-favorite-heart-icon>
        </div>
      </div>
    </div>
  `

  css = () => `
    :host {
      width: ${this.dataWidth || 'fit-content'};
    }
    .lvc-button-medium {
      font-family: ${Font.family};
      display: flex;
      height: ${this.dataButtonSize === 'large' ? '52px' : '40px'};
      padding-right: ${this.dataButtonSize === 'large' ? '15px' : '1rem'};
      padding-left: ${this.dataButtonSize === 'large' ? '20px' : '1rem'};
      justify-content: center;
      align-items: center;
      border-radius: 99px;
      background-color: #fff;
      transition: box-shadow 200ms ease;
      font-size: ${this.dataButtonSize === 'large' ? '14px' : '12px'};
      text-decoration: none;
      cursor: pointer;
    }

    lvc-favorite-heart-icon {
      width: ${this.dataButtonSize === 'large' ? '44px' : '33px'};
      height: ${this.dataButtonSize === 'large' ? '44px' : '33px'};
    }

    .lvc-button-medium:hover {
      box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 14px 20px 0px rgba(30, 0, 125, 0.18);
    }

    .lvc-button-medium.isblue {
      background-color: #1e007d;
      color: #fff;
    }

    .lvc-button-medium_icon {
      margin-left: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .unliked {
      width: auto;
      height: auto;
    }

    .icon {
      width: ${this.dataButtonSize === 'large' ? '44px' : '33px'};
      height: ${this.dataButtonSize === 'large' ? '44px' : '33px'};
    }

    #button:not(.liked) .liked, #button.liked .unliked {
      display: none;
    }

    ${
      this.dataButtonMobileFloat
        ? `
        @media only screen and (max-width: 991px) {
          #button {
            box-shadow: 0px 14px 20px rgba(30, 0, 125, 0.25);
          }
        }
        `
        : ''
    }
  `

  eventListeners: EventListeners = [
    ['#button', 'click', this.onClick],
    [window, 'lvc:item-favorite-status-update', this.onFavoriteStatusUpdate],
    [
      window,
      'lvc:user-data-updated',
      event => this.updateFavoritesData(event, this.dataId, this.dataType),
    ],
  ]
}
